/**
 * SVG Graph Controller (EPIC-026.5)
 *
 * Renders a small native SVG line chart inside the maquette `nm-ph` placeholder.
 * Two series:
 *  - Cumulated CA (toutes activités) — accent stroke
 *  - VAT franchise threshold — dashed neutral stroke
 *
 * Optional milestone markers (small circles on the X axis with `<title>` tooltip).
 *
 * Input is provided via `setData({ months, caCumul, vatThreshold, milestones })`:
 *  - months: number of months on the X axis (12 / 24 / 36)
 *  - caCumul: array of cumulated CA per month (length = months + 1, [0] = 0)
 *  - vatThreshold: scalar threshold (e.g. 37 500)
 *  - milestones: [{ monthIndex, label, color }]
 */

import BaseController from "./base_controller.js";

const SVG_NS = "http://www.w3.org/2000/svg";

const COLORS = {
  ca: "#5b3aa8",
  threshold: "#7e8a99",
  axis: "#cfd6df",
  text: "#5a6473",
  marker: "#d9531f",
};

export default class SvgGraphController extends BaseController {
  static values = {
    height: { type: Number, default: 280 },
  };

  connect() {
    this._graphData = {
      months: 12,
      caCumul: [],
      vatThreshold: 37500,
      milestones: [],
    };
    this.render();
  }

  setData(data) {
    this._graphData = { ...this._graphData, ...data };
    this.render();
  }

  render() {
    const root = this.element;
    while (root.firstChild) {
      root.removeChild(root.firstChild);
    }

    const width = root.clientWidth || 600;
    const height = this.heightValue || 280;
    const padding = { top: 16, right: 16, bottom: 36, left: 56 };
    const innerW = Math.max(width - padding.left - padding.right, 100);
    const innerH = Math.max(height - padding.top - padding.bottom, 80);

    const { months, caCumul, vatThreshold, milestones } = this._graphData;
    const n = Math.max(1, Math.min(36, months));
    const series = caCumul && caCumul.length > 0 ? caCumul.slice(0, n + 1) : [0];
    const maxCa = series.reduce((m, v) => (v > m ? v : m), 0);
    const yMax = Math.max(maxCa, vatThreshold || 0) * 1.1 || 1000;

    const xFor = (i) => padding.left + (innerW * i) / n;
    const yFor = (v) => padding.top + innerH - (innerH * v) / yMax;

    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("width", "100%");
    svg.setAttribute("height", String(height));
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    svg.setAttribute("role", "img");
    svg.setAttribute("aria-label", "Projection du chiffre d'affaires cumulé et seuil de franchise TVA");

    appendAxes(svg, padding, innerW, innerH, n, yMax, height);

    const thresholdLine = createPath([
      [xFor(0), yFor(vatThreshold)],
      [xFor(n), yFor(vatThreshold)],
    ]);
    thresholdLine.setAttribute("stroke", COLORS.threshold);
    thresholdLine.setAttribute("stroke-width", "1.5");
    thresholdLine.setAttribute("stroke-dasharray", "6 4");
    thresholdLine.setAttribute("fill", "none");
    svg.appendChild(thresholdLine);

    const caPoints = series.map((v, i) => [xFor(i), yFor(v)]);
    const caPath = createPath(caPoints);
    caPath.setAttribute("stroke", COLORS.ca);
    caPath.setAttribute("stroke-width", "2.5");
    caPath.setAttribute("fill", "none");
    svg.appendChild(caPath);

    (milestones || []).forEach((m) => {
      if (m.monthIndex < 0 || m.monthIndex > n) return;
      const marker = document.createElementNS(SVG_NS, "circle");
      marker.setAttribute("cx", String(xFor(m.monthIndex)));
      marker.setAttribute("cy", String(padding.top + innerH + 8));
      marker.setAttribute("r", "5");
      marker.setAttribute("fill", m.color || COLORS.marker);
      marker.setAttribute("stroke", "#fff");
      marker.setAttribute("stroke-width", "1.5");
      const title = document.createElementNS(SVG_NS, "title");
      title.textContent = m.label || "";
      marker.appendChild(title);
      svg.appendChild(marker);
    });

    appendLegend(svg, width, height);

    root.appendChild(svg);
  }

  disconnect() {
    while (this.element.firstChild) {
      this.element.removeChild(this.element.firstChild);
    }
  }
}

function createPath(points) {
  const path = document.createElementNS(SVG_NS, "path");
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`)
    .join(" ");
  path.setAttribute("d", d);
  return path;
}

function appendAxes(svg, padding, innerW, innerH, months, yMax, height) {
  const xAxis = document.createElementNS(SVG_NS, "line");
  xAxis.setAttribute("x1", String(padding.left));
  xAxis.setAttribute("y1", String(padding.top + innerH));
  xAxis.setAttribute("x2", String(padding.left + innerW));
  xAxis.setAttribute("y2", String(padding.top + innerH));
  xAxis.setAttribute("stroke", COLORS.axis);
  xAxis.setAttribute("stroke-width", "1");
  svg.appendChild(xAxis);

  const yAxis = document.createElementNS(SVG_NS, "line");
  yAxis.setAttribute("x1", String(padding.left));
  yAxis.setAttribute("y1", String(padding.top));
  yAxis.setAttribute("x2", String(padding.left));
  yAxis.setAttribute("y2", String(padding.top + innerH));
  yAxis.setAttribute("stroke", COLORS.axis);
  yAxis.setAttribute("stroke-width", "1");
  svg.appendChild(yAxis);

  const xStep = months >= 24 ? 6 : 3;
  for (let i = 0; i <= months; i += xStep) {
    const x = padding.left + (innerW * i) / months;
    const tick = document.createElementNS(SVG_NS, "line");
    tick.setAttribute("x1", String(x));
    tick.setAttribute("y1", String(padding.top + innerH));
    tick.setAttribute("x2", String(x));
    tick.setAttribute("y2", String(padding.top + innerH + 4));
    tick.setAttribute("stroke", COLORS.axis);
    svg.appendChild(tick);
    const label = document.createElementNS(SVG_NS, "text");
    label.setAttribute("x", String(x));
    label.setAttribute("y", String(padding.top + innerH + 18));
    label.setAttribute("text-anchor", "middle");
    label.setAttribute("font-size", "11");
    label.setAttribute("fill", COLORS.text);
    label.setAttribute("font-family", "var(--mono), monospace");
    label.textContent = `M${i}`;
    svg.appendChild(label);
  }

  const ySteps = 4;
  for (let s = 0; s <= ySteps; s += 1) {
    const v = (yMax * s) / ySteps;
    const y = padding.top + innerH - (innerH * s) / ySteps;
    const grid = document.createElementNS(SVG_NS, "line");
    grid.setAttribute("x1", String(padding.left));
    grid.setAttribute("y1", String(y));
    grid.setAttribute("x2", String(padding.left + innerW));
    grid.setAttribute("y2", String(y));
    grid.setAttribute("stroke", COLORS.axis);
    grid.setAttribute("stroke-dasharray", "2 4");
    grid.setAttribute("stroke-width", "0.6");
    svg.appendChild(grid);
    const label = document.createElementNS(SVG_NS, "text");
    label.setAttribute("x", String(padding.left - 8));
    label.setAttribute("y", String(y + 4));
    label.setAttribute("text-anchor", "end");
    label.setAttribute("font-size", "11");
    label.setAttribute("fill", COLORS.text);
    label.setAttribute("font-family", "var(--mono), monospace");
    label.textContent = formatEur(v);
    svg.appendChild(label);
  }
}

function appendLegend(svg, width, height) {
  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("transform", `translate(${width - 220}, ${height - 18})`);

  const ca = document.createElementNS(SVG_NS, "line");
  ca.setAttribute("x1", "0");
  ca.setAttribute("y1", "0");
  ca.setAttribute("x2", "20");
  ca.setAttribute("y2", "0");
  ca.setAttribute("stroke", COLORS.ca);
  ca.setAttribute("stroke-width", "2.5");
  g.appendChild(ca);

  const caLabel = document.createElementNS(SVG_NS, "text");
  caLabel.setAttribute("x", "26");
  caLabel.setAttribute("y", "4");
  caLabel.setAttribute("font-size", "11");
  caLabel.setAttribute("fill", COLORS.text);
  caLabel.textContent = "CA cumulé";
  g.appendChild(caLabel);

  const th = document.createElementNS(SVG_NS, "line");
  th.setAttribute("x1", "110");
  th.setAttribute("y1", "0");
  th.setAttribute("x2", "130");
  th.setAttribute("y2", "0");
  th.setAttribute("stroke", COLORS.threshold);
  th.setAttribute("stroke-width", "1.5");
  th.setAttribute("stroke-dasharray", "4 3");
  g.appendChild(th);

  const thLabel = document.createElementNS(SVG_NS, "text");
  thLabel.setAttribute("x", "136");
  thLabel.setAttribute("y", "4");
  thLabel.setAttribute("font-size", "11");
  thLabel.setAttribute("fill", COLORS.text);
  thLabel.textContent = "Seuil franchise TVA";
  g.appendChild(thLabel);

  svg.appendChild(g);
}

function formatEur(value) {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)} k€`;
  }
  return `${value.toFixed(0)} €`;
}
